import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { WithDoWithClosestHandler } from '@bingads-webui-react/hoc-with-do-with-closest-handler';
import { CustomColumnParser } from '@bingads-webui/custom-column-parser';

import { validFormats, formValidationSchema } from './config';
import {
  tokenize,
  applyPropertyConversions,
  columnToken,
  spliceTokens,
  operatorToken,
  tokensToString,
  opsTitleToValue,
  opsValueToTitle,
  addTokenToExpression,
  expressionTokenTypes,
  showTokenSuggestions,
  prevTokenIndexByTypes,
  nextTokenIndexByTypes,
  removeTokenFromExpression,
  replaceTokenWithinExpression,
} from './token';
import { sharedProps, sharedDefaults } from './props';

const managedInputKeys = [
  '+', '-', '*', '/', '(', ')',
  'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowDown',
];
const managedMetricKeys = [
  '+', '-', '*', '/', '(', ')',
  'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
];
const operators = '+-*/()';
const nonParenthesisOperators = '+-*/';
const maxOperatorsCount = 20;

/**
 * Props consumed by this component (not passed to child)
 */
const hocProps = {
  initialExpression: PropTypes.arrayOf(PropTypes.string),
  initialName: PropTypes.string,
  initialDescription: PropTypes.string,
  initialColumnFormat: PropTypes.oneOf(validFormats),
};
const hocDefaults = {
  initialExpression: [''],
  initialName: '',
  initialDescription: '',
  initialColumnFormat: _.first(validFormats),
};

/**
 * @param {React.Component} WrappedComponent - component to wrap
 * @returns {React.Component} an enhanced version of the original component
 */
export const WithExpressionEditorControls = (WrappedComponent) => {
  const { TEXT, COLUMN } = expressionTokenTypes;

  class HoC extends React.PureComponent {
    static displayName = `WithExpressionEditorControls(${getDisplayName(WrappedComponent)})`;
    static propTypes = PropTypes.shape(_.defaults({}, hocProps, sharedProps)).isRequired;
    static defaultProps = _.defaults(hocDefaults, sharedDefaults);

    state = {
      name: this.props.initialName,
      description: this.props.initialDescription,
      expressionTokens: applyPropertyConversions(
        tokenize(this.props.initialExpression, this.props),
        this.props
      ),
      isFormDirty: false,
      isFormValid: false,
      formErrorMessages: [],
      currentFocus: -1,
      mostRecentMetricFocus: -1,
      columnFormat: this.props.initialColumnFormat,
    };

    componentDidMount() {
      window.addEventListener('keyup', this.handleGlobalKeyup);
    }

    componentDidUpdate(prevProps, prevState) {
      const { expressionTokens, currentFocus } = this.state;

      if (currentFocus >= 0 &&
        currentFocus < expressionTokens.length &&
        prevState.currentFocus !== currentFocus &&
        prevState.mostRecentMetricFocus !== currentFocus &&
        expressionTokens[currentFocus].type === COLUMN
      ) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ mostRecentMetricFocus: currentFocus });
      }
    }

    componentWillUnmount() {
      window.removeEventListener('keyup', this.handleGlobalKeyup);
    }

    setFocusHandlerFactory = _.memoize(index => (e) => {
      _.result(e, 'stopPropagation');
      this.setState({ currentFocus: index });
    });

    setFocus = (index, ref = null) => {
      const { currentFocus } = this.state;
      if (currentFocus === index) {
        if (ref) {
          ref.focus();
        }
      } else {
        this.setState({
          currentFocus: index,
        }, (ref && index >= 0) ? (() => {
          ref.focus();
        }) : undefined);
      }
    };

    parser = new CustomColumnParser(_.pick(this.props, 'metricCategories', 'segmentCategories'));

    removeIndexFromExpressionFactory = _.memoize(index => () =>
      this.setState(({ expressionTokens, currentFocus }) => {
        const updatedState = {};

        if (index < expressionTokens.length) {
          updatedState.expressionTokens = removeTokenFromExpression(expressionTokens, index);
          updatedState.currentFocus = currentFocus === index ? -1 : currentFocus - 1;
        }

        return updatedState;
      }));

    handleFormValueChanged = ({ target: { name, value } }) => this.setState({ [name]: value })

    handleFormValidate = ({
      isValid: isFormValid,
      isDirty: isFormDirty,
      messages,
    }) =>
      this.setState(({ formErrorMessages }) => {
        const updatedState = { isFormValid, isFormDirty };

        if (_.isEmpty(messages) && !_.isEmpty(formErrorMessages)) {
          updatedState.formErrorMessages = [];
        } else if (_.difference(messages, formErrorMessages).length > 0) {
          // Only update if error messages change
          updatedState.formErrorMessages = messages;
        }

        return updatedState;
      });

    handleMetricKeyUpFactory = _.memoize(index => (e) => {
      if (_.contains(managedMetricKeys, e.key)) {
        e.stopPropagation();
        e.preventDefault();
      }

      e.persist();

      this.setState(({ expressionTokens, currentFocus }) => {
        const updatedState = {};

        // Because of padding rules, the minimum index for a metric is 1
        if (index > 0 && index < expressionTokens.length) {
          switch (e.key) {
            case '+':
            case '-':
            case '*':
            case '/':
            case '(':
            case ')': {
              let updatedExpr = [...expressionTokens];
              const newToken = operatorToken({
                value: e.key,
                title: opsValueToTitle[e.key],
              });

              updatedExpr = addTokenToExpression(updatedExpr, currentFocus + 1, newToken);
              updatedState.expressionTokens = updatedExpr;
              updatedState.currentFocus = _.indexOf(updatedExpr, newToken) + 1;
              break;
            }
            case 'Backspace':
            case 'Delete': {
              updatedState.expressionTokens = removeTokenFromExpression(expressionTokens, index);
              updatedState.currentFocus = currentFocus - 1;
              break;
            }
            case 'ArrowLeft': {
              updatedState.currentFocus =
                prevTokenIndexByTypes(expressionTokens, index, [TEXT, COLUMN]);
              break;
            }
            case 'ArrowRight': {
              updatedState.currentFocus =
                nextTokenIndexByTypes(expressionTokens, index, [TEXT, COLUMN]);
              break;
            }
            default:
              break;
          }
        }

        return updatedState;
      });
    });

    handleInputClickedFactory = _.memoize(index => () =>
      this.setState(({ expressionTokens }) => ({
        currentFocus: index,
        expressionTokens: showTokenSuggestions(expressionTokens, index),
      })));

    /**
     * When traversing an expression using the keyboard, an action that
     * causes the cursor position to arrive at the beginning (or at the end)
     * of an input field  can cause the focus to "jump" to the previous (or
     * to the next) input field. These flags act to "debounce" the related
     * keyboard actions in the given scenarios.
     */
    preventLeft = true;
    preventRight = true;

    handleInputKeyUpFactory = _.memoize(index => (e) => {
      if (_.contains(managedInputKeys, e.key)) {
        e.stopPropagation();
      }

      e.persist(); // Required for e to be available in setState callback

      this.setState(({ expressionTokens, currentFocus }) => {
        const updatedState = {};

        if (index >= 0 && currentFocus >= 0 && currentFocus < expressionTokens.length) {
          const focusedToken = expressionTokens[currentFocus];
          const cursorAtStart = e.target.selectionStart < 1;
          const cursorAtEnd = e.target.selectionStart > focusedToken.value.length - 1;

          switch (e.key) {
            case 'Home': {
              updatedState.currentFocus = 0;
              break;
            }
            case 'End': {
              updatedState.currentFocus = expressionTokens.length - 1;
              break;
            }
            case 'Shift': // user can release shift before the operator keys
            case '+':
            case '-':
            case '*':
            case '/':
            case '(':
            case ')': {
              const inputValue = e.target.value;
              const lastKey = inputValue[inputValue.length - 1];
              if (lastKey && operators.indexOf(lastKey) !== -1) {
                const newValues = inputValue.split(lastKey);
                newValues.splice(1, 0, lastKey);
                const tokensSplitByType = tokenize(newValues);

                updatedState.expressionTokens =
                  spliceTokens(expressionTokens, currentFocus, tokensSplitByType);
                updatedState.currentFocus =
                  _.indexOf(updatedState.expressionTokens, _.last(tokensSplitByType));
              }
              break;
            }
            case 'Backspace': {
              if (!cursorAtStart) {
                this.preventLeft = focusedToken.value.length > 0;
              } else {
                e.preventDefault();

                if (this.preventLeft) {
                  this.preventLeft = false;
                } else {
                  const updatedTokens = removeTokenFromExpression(expressionTokens, index - 1);
                  let updatedFocus = currentFocus > 0 ? currentFocus - 1 : currentFocus;

                  while (updatedFocus > 0
                    && _.result(updatedTokens[updatedFocus], 'type') !== TEXT) {
                    updatedFocus -= 1;
                  }

                  updatedState.expressionTokens = updatedTokens;
                  updatedState.currentFocus = updatedFocus;
                }
              }

              break;
            }
            case 'Delete': {
              if (cursorAtEnd) {
                e.preventDefault();

                if (this.preventRight) {
                  this.preventRight = false;
                } else {
                  updatedState.expressionTokens =
                    removeTokenFromExpression(expressionTokens, index + 1);
                }
              } else {
                this.preventRight = focusedToken.value.length > 0;
              }
              break;
            }
            case 'ArrowLeft': {
              if (cursorAtStart) {
                e.preventDefault();

                if (this.preventLeft) {
                  this.preventLeft = false;
                } else {
                  updatedState.currentFocus =
                    prevTokenIndexByTypes(expressionTokens, index, [TEXT, COLUMN]);
                }
              } else {
                this.preventLeft = focusedToken.value.length > 0;
              }
              break;
            }
            case 'ArrowRight': {
              if (cursorAtEnd) {
                e.preventDefault();

                if (this.preventRight) {
                  this.preventRight = false;
                } else {
                  updatedState.currentFocus =
                    nextTokenIndexByTypes(expressionTokens, index, [TEXT, COLUMN]);
                }
              } else {
                this.preventRight = focusedToken.value.length > 0;
              }
              break;
            }
            case 'ArrowDown': {
              if (!expressionTokens[index].showSuggestions) {
                updatedState.expressionTokens =
                  showTokenSuggestions(expressionTokens, index);
              }
              break;
            }
            default:
              this.preventLeft = focusedToken.value.length > 0;
              this.preventRight = focusedToken.value.length > 0 && !cursorAtEnd;
              break;
          }
        }

        return updatedState;
      });
    });

    handleInputChangedFactory = _.memoize(index => (e) => {
      e.persist();

      this.setState(({ expressionTokens }) => {
        const { value } = e.target;
        const updatedToken = _.defaults({
          value,
          showSuggestions: !_.isEmpty(value),
        }, expressionTokens[index]);

        return {
          expressionTokens: replaceTokenWithinExpression(expressionTokens, index, updatedToken),
        };
      });
    });

    hideInputSuggestions = () =>
      this.setState(({ expressionTokens }) => ({
        expressionTokens: _.map(expressionTokens, token => (token.showSuggestions ? _.defaults({
          showSuggestions: false,
        }, token) : token)),
      }));

    handleSegmentChange = _.memoize(({
      categoryValue,
      categoryTitle,
      value,
      title,
    }) => () => this.setState(({ expressionTokens, mostRecentMetricFocus }) => {
      const stateUpdates = {};

      if (
        mostRecentMetricFocus > 0
        && mostRecentMetricFocus < expressionTokens.length - 1
        && expressionTokens[mostRecentMetricFocus].type === COLUMN
      ) {
        let { segments } = expressionTokens[mostRecentMetricFocus];

        if (!_.isEmpty(segments) && segments.categoryValue === categoryValue) {
          if (_.findWhere(segments.selections, { value })) {
            segments.selections = _.reject(segments.selections, seg => seg.value === value);

            if (_.isEmpty(segments.selections)) {
              segments = null;
            }
          } else {
            segments.selections.push({ value, title });
          }
        } else {
          segments = {
            categoryValue,
            categoryTitle,
            selections: [{ value, title }],
          };
        }

        const updatedToken = _.defaults({ segments }, expressionTokens[mostRecentMetricFocus]);
        stateUpdates.expressionTokens = replaceTokenWithinExpression(
          expressionTokens,
          mostRecentMetricFocus,
          updatedToken
        );
      }

      return stateUpdates;
    }), ({
      categoryValue,
      categoryTitle,
      value,
      title,
    }) => `${categoryValue}${categoryTitle}${value}${title}`);

    changeInputToSelectedMetricFactory = _.memoize(index => ({ value, title }) =>
      this.setState(({ expressionTokens }) => {
        const token = columnToken({ value, title });
        const updatedTokens = replaceTokenWithinExpression(expressionTokens, index, token);

        return {
          expressionTokens: updatedTokens,
          currentFocus: _.indexOf(updatedTokens, token),
        };
      }));

    insertMetric = ({ value, title }) =>
      this.setState(({ expressionTokens, currentFocus }) => {
        const newToken = columnToken({ value, title });
        const updatedExpr = addTokenToExpression(expressionTokens, currentFocus + 1, newToken);

        return {
          expressionTokens: updatedExpr,
          currentFocus: _.indexOf(updatedExpr, newToken),
        };
      });

    insertOperator = nodeValue =>
      this.setState(({ expressionTokens, currentFocus }) => {
        const newToken = operatorToken({
          value: opsTitleToValue[nodeValue],
          title: nodeValue,
        });
        const updatedExpr = addTokenToExpression(expressionTokens, currentFocus + 1, newToken);

        return {
          expressionTokens: updatedExpr,
          currentFocus: _.indexOf(updatedExpr, newToken) + 1,
        };
      });

    handleGlobalKeyup = (e) => {
      switch (e.key) {
        case 'Escape':
          this.hideInputSuggestions();
          break;
        default:
          break;
      }
    };

    render() {
      const expressionString = tokensToString(this.state.expressionTokens);
      const passThroughProps = _.omit(this.props, ...Object.keys(hocProps));
      const tokenCount = _.chain(this.state.expressionTokens)
        .filter(token => token.type === 'operator' && nonParenthesisOperators.indexOf(token.value) !== -1)
        .size().value();
      const isExpressionTooLong = tokenCount > maxOperatorsCount;
      const isValid = this.parser.isValid(expressionString) && !isExpressionTooLong;
      const isFormValid = this.state.isFormValid && isValid;

      return (
        <WrappedComponent
          {...{
            containerRef: this.props.containerRef,
            currentFocus: this.state.currentFocus,
            mostRecentMetricFocus: this.state.mostRecentMetricFocus,
            customColumnFormSchema: formValidationSchema,
            customColumnFormErrorMessages: this.state.formErrorMessages,
            customColumnFormIsDirty: this.state.isFormDirty,
            doWithClosest: this.props.doWithClosest,
            expressionString,
            expressionTokens: this.state.expressionTokens,
            formValueColumnFormat: this.state.columnFormat,
            formValueDescription: this.state.description,
            formValueName: this.state.name,
            isFormValid,
            isValid,
            isExpressionTooLong,
            onDropdownValueSelectedFactory: this.changeInputToSelectedMetricFactory,
            onFocusFactory: this.setFocusHandlerFactory,
            onFormValidate: this.handleFormValidate,
            onFormValueChange: this.handleFormValueChanged,
            onInputChangeFactory: this.handleInputChangedFactory,
            onInputClickFactory: this.handleInputClickedFactory,
            onInputKeyUpFactory: this.handleInputKeyUpFactory,
            onMetricKeyUpFactory: this.handleMetricKeyUpFactory,
            onMetricRemoveButtonClickFactory: this.removeIndexFromExpressionFactory,
            onMetricSelected: this.insertMetric,
            onOperatorSelected: this.insertOperator,
            onSegmentChangedFactory: this.handleSegmentChange,
            setChildRefByIndex: this.props.setChildRefByIndex,
            setChildRefCount: this.props.setChildRefCount,
            setFocus: this.setFocus,
          }}
          {...passThroughProps}
        />
      );
    }
  }

  return hoistNonReactStatics(WithDoWithClosestHandler(HoC), WrappedComponent);
};
